import { DataTexture, RGBAFormat, UnsignedByteType } from "three";

/*
 * Copyright Higharc Inc. 2019
 */
import * as THREE from "three";
import type { THREEMeshMaterial } from "../../materials/designMaterialParams";
import { assignMaterialSharedDefines, MaterialSharedDefines } from "./MaterialSharedDefines";
import { SSFXRendererOutput } from "./SSFXRendererOutput";

// Lazy-initialized 1x1 black texture for highlightedIds fallback.
// Using an explicit black texture instead of null avoids Three.js fallback texture issues.
let emptyHighlightTexture: DataTexture | undefined;
export function getEmptyHighlightTexture(): DataTexture {
  if (!emptyHighlightTexture) {
    const data = new Uint8Array(4); // All zeros = black
    emptyHighlightTexture = new DataTexture(data, 1, 1, RGBAFormat, UnsignedByteType);
    emptyHighlightTexture.needsUpdate = true;
  }
  return emptyHighlightTexture;
}

export const SOFT_SHADOW_CAMERA_FAR_DEFAULT = 500;
export const PROBE_GRID_NORMAL_OFFSET_MULTIPLIER_DEFAULT = 0.2;
export const PROBE_GRID_SMOOTH_BACKFACE_TERM_CONSTANT_TERM_DEFAULT = 0.9;

interface SharedUniforms {
  ssfxEnabled: THREE.IUniform;
  ssfxMaterialOutputType: THREE.IUniform;

  aoMapPixelOffset: THREE.IUniform;
  aoMapInvSize: THREE.IUniform;

  renderTargetResolution: THREE.IUniform;
  screenSpaceAoMap: THREE.IUniform;
  screenSpaceReflectionMap: THREE.IUniform;
  screenSpaceShadowMap: THREE.IUniform;

  substitutedReflectionMultiplier: THREE.IUniform;

  ssfxToneMappingEnabled: THREE.IUniform;
  toneMappingExposure: THREE.IUniform;

  randomSeed: THREE.IUniform;
  sampleIndex: THREE.IUniform; // SSAA sample index. Some effects can start as a rough approximation and use tweaked parameters after more samples are accumulated.

  lightProbeIrradianceMultiplier: THREE.IUniform;

  probeGridNormalOffsetMultiplier: THREE.IUniform;
  probeGridSmoothBackfaceWeightConstantTerm: THREE.IUniform;

  softShadowCameraFar: THREE.IUniform;

  highlightedIds: THREE.IUniform;

  radialFogParams: THREE.IUniform;

  isolateMode: THREE.IUniform;
}

export interface SharedUniformValues {
  outputType: SSFXRendererOutput;

  // This acts as a top-level toggle for the different screen space effects.
  // It must be enabled for SSAO and SSR to be enabled.
  ssfxEnabled: boolean;

  renderTargetResolution: [number, number];
  screenSpaceAoMap: THREE.Texture | null;
  screenSpaceReflectionMap: THREE.Texture | null;
  screenSpaceShadowMap: THREE.Texture | null;

  substitutedReflectionMultiplier: number;

  ssfxToneMappingEnabled: boolean;
  toneMappingExposure: number;

  lightProbeIrradianceMultiplier: number;

  probeGridNormalOffsetMultiplier: number;
  probeGridSmoothBackfaceWeightConstantTerm: number;

  softShadowCameraFar: number;

  highlightedIds: THREE.Texture | null;

  radialFogParams: {
    centerX: number;
    centerY: number;
    near: number;
    far: number;
  };

  isolateMode: boolean;
}

export function assignSharedDefines(materialGroup: MaterialSharedParameterGroup, material: THREEMeshMaterial) {
  assignMaterialSharedDefines(materialGroup.sharedDefines, material);
}

// Instances of materials in the same group share the same screen space effects and the same lighting settings etc.
export class MaterialSharedParameterGroup {
  private readonly sharedUniforms: SharedUniforms;

  readonly sharedDefines: MaterialSharedDefines = new MaterialSharedDefines();

  readonly materials: THREEMeshMaterial[] = [];

  private outputTypeInternal: SSFXRendererOutput = SSFXRendererOutput.FINAL_RENDER_WITH_EFFECTS;

  private static instanceInternal: MaterialSharedParameterGroup | undefined;

  // Having a singleton material group enables us to keep using the same scene and materials even if the viewer changes.
  static get instance(): MaterialSharedParameterGroup {
    return this.instanceInternal ?? (this.instanceInternal = new MaterialSharedParameterGroup());
  }

  private constructor() {
    // These uniform values are shared between the material instances in this group.
    this.sharedUniforms = {
      // Screen space effect uniforms.
      ssfxEnabled: { value: true },
      ssfxMaterialOutputType: { value: this.outputTypeInternal },

      renderTargetResolution: { value: new THREE.Vector2(256, 256) },
      screenSpaceAoMap: { value: null },
      aoMapPixelOffset: { value: new THREE.Vector2(0, 0) },
      aoMapInvSize: { value: new THREE.Vector2(1 / 256, 1 / 256) },
      screenSpaceReflectionMap: { value: null },
      screenSpaceShadowMap: { value: null },

      substitutedReflectionMultiplier: { value: 0.25 },

      // Disable / enable tonemapping at runtime.
      ssfxToneMappingEnabled: { value: true },
      toneMappingExposure: { value: 1.0 },

      // Set per frame
      randomSeed: { value: 0.0 },
      sampleIndex: { value: 0 },

      // Light probe uniforms.
      lightProbeIrradianceMultiplier: { value: 1.0 },

      // Light probe lookup offset along surface normal in world space. This is a fast method to mitigate light leaks in some common situations.
      probeGridNormalOffsetMultiplier: { value: PROBE_GRID_NORMAL_OFFSET_MULTIPLIER_DEFAULT },

      // Value that's added to the smooth backface term before the light probe weight is multiplied by it.
      // A higher value means that the smooth backface term affects the light probe weights less.
      probeGridSmoothBackfaceWeightConstantTerm: { value: PROBE_GRID_SMOOTH_BACKFACE_TERM_CONSTANT_TERM_DEFAULT },

      softShadowCameraFar: { value: SOFT_SHADOW_CAMERA_FAR_DEFAULT },

      // Should be a 256x256 texture, where the red channel of each pixel records whether that entity id is highlighted.
      // Ids are in row-major order. Using explicit black texture instead of null to avoid Three.js fallback issues.
      highlightedIds: { value: getEmptyHighlightTexture() },

      radialFogParams: { value: new THREE.Vector4(0, 0, 1000000, 10000000) },

      isolateMode: { value: false },
    };
  }

  useSharedUniformsInMaterial(uniforms: { [name: string]: any }) {
    for (const key of Object.keys(this.sharedUniforms)) {
      uniforms[key] = this.sharedUniforms[key];
    }
  }

  getHighlightTexture(): THREE.Texture {
    return this.sharedUniforms.highlightedIds.value ?? getEmptyHighlightTexture();
  }

  setAoMapDimensions({ aoMapPixelOffset, aoMapSize }: { aoMapPixelOffset: THREE.Vector2; aoMapSize: THREE.Vector2 }) {
    this.sharedUniforms.aoMapPixelOffset.value.set(aoMapPixelOffset.x, aoMapPixelOffset.y);
    this.sharedUniforms.aoMapInvSize.value.set(1.0 / aoMapSize.x, 1.0 / aoMapSize.y);
  }

  /**
   * Set most shared uniform values with a single call - this ensures that all of the uniforms have valid values.
   */
  setSharedUniformValues(uniformValues: SharedUniformValues) {
    this.outputType = uniformValues.outputType;
    this.sharedUniforms.ssfxEnabled.value = uniformValues.ssfxEnabled;
    this.sharedUniforms.renderTargetResolution.value.set(
      uniformValues.renderTargetResolution[0],
      uniformValues.renderTargetResolution[1],
    );
    this.sharedUniforms.screenSpaceAoMap.value = uniformValues.screenSpaceAoMap;
    this.sharedUniforms.screenSpaceReflectionMap.value = uniformValues.screenSpaceReflectionMap;
    this.sharedUniforms.screenSpaceShadowMap.value = uniformValues.screenSpaceShadowMap;

    this.sharedUniforms.substitutedReflectionMultiplier.value = uniformValues.substitutedReflectionMultiplier;

    this.sharedUniforms.ssfxToneMappingEnabled.value = uniformValues.ssfxToneMappingEnabled;
    this.sharedUniforms.toneMappingExposure.value = uniformValues.toneMappingExposure;

    this.sharedUniforms.lightProbeIrradianceMultiplier.value = uniformValues.lightProbeIrradianceMultiplier;

    this.sharedUniforms.probeGridNormalOffsetMultiplier.value = uniformValues.probeGridNormalOffsetMultiplier;
    this.sharedUniforms.probeGridSmoothBackfaceWeightConstantTerm.value =
      uniformValues.probeGridSmoothBackfaceWeightConstantTerm;

    this.sharedUniforms.softShadowCameraFar.value = uniformValues.softShadowCameraFar;

    this.sharedUniforms.highlightedIds.value = uniformValues.highlightedIds;

    this.sharedUniforms.radialFogParams.value.set(
      uniformValues.radialFogParams.centerX,
      uniformValues.radialFogParams.centerY,
      uniformValues.radialFogParams.near,
      uniformValues.radialFogParams.far,
    );

    this.sharedUniforms.isolateMode.value = uniformValues.isolateMode;
  }

  set randomSeed(value: number) {
    this.sharedUniforms.randomSeed.value = value;
  }

  set sampleIndex(value: number) {
    this.sharedUniforms.sampleIndex.value = value;
  }

  private set outputType(value: SSFXRendererOutput) {
    this.outputTypeInternal = value;
    this.sharedUniforms["ssfxMaterialOutputType"].value = this.outputTypeInternal;
    this.sharedDefines.outputType = this.outputTypeInternal;
  }
}
